import React, { useState } from 'react';
import { useEditor, docIsDirty, isPristineDoc } from '../../context/EditorContext';
import { usePublicationActions } from '../../hooks/usePublicationActions';
import { ClosePublicationDialog } from './BackstagePanels';
import { ContextMenu, ContextMenuCheckboxItem, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from '../ui/context-menu';
import { Cloud, FileText, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { useLibraryIndex, folderOfProject } from '../../lib/libraryIndex';
import { folderColorCss } from '../../lib/avatar';

/**
 * Document tabs — every open publication has a tab (browser style) above the ribbon. Opening / creating / importing
 * a publication opens a new tab; the one in progress stays open until its tab is closed. Dirty tabs show a dot and ask
 * to save before closing. Saved publications that live in a folder carry the folder's colour (dot + tooltip path) so
 * nested client work is recognisable at a glance, and — unless switched off from a tab's context menu — tabs are shown
 * grouped by folder with a coloured divider + folder name in front of each group (the user's own order is kept inside a
 * group; the groups appear in the order their first tab was opened). Publications without a folder form a "No folder" group.
 */
const GROUP_KEY = 'wp.tabs.groupByFolder';
const NO_FOLDER = '__none';
export const DocumentTabs = ({ inBackstage = false }) => {
  const { state, rawDispatch, hasUnsavedChanges } = useEditor();
  const { closePublication } = usePublicationActions();
  const [closeOpen, setCloseOpen] = useState(false);
  const [dragId, setDragId] = useState(null);
  const library = useLibraryIndex(); // folders + { id, folder_id } of every live publication
  const [grouped, setGrouped] = useState(() => { try { return localStorage.getItem(GROUP_KEY) !== '0'; } catch { return true; } });
  const toggleGrouped = () => setGrouped((g) => { try { localStorage.setItem(GROUP_KEY, g ? '0' : '1'); } catch { /* private mode */ } return !g; });
  const tabs = state.docTabs || [];
  const active = state.activeTabId;

  const infoOf = (t) => {
    if (t.id === active) return { name: state.projectName || 'Untitled Publication', saved: !!state.projectId, dirty: hasUnsavedChanges, pristine: isPristineDoc(state), folder: folderOfProject(library, state.projectId) };
    const d = t.doc || {};
    return { name: d.projectName || 'Untitled Publication', saved: !!d.projectId, dirty: docIsDirty(d), pristine: !!t.doc && isPristineDoc(d), folder: folderOfProject(library, d.projectId) };
  };

  const switchTo = (id) => { if (id !== active) rawDispatch({ type: 'SWITCH_DOC_TAB', payload: { id } }); };

  /** Close a tab: clean tabs close at once; a dirty one is brought to the front and asks to save first. */
  const requestClose = (id) => {
    const t = tabs.find((x) => x.id === id);
    if (!t) return;
    const info = infoOf(t);
    if (!info.dirty) { closePublication(id); if (tabs.length > 1) toast.success(`Closed “${info.name}”`, { id: 'doc-tab-close' }); return; }
    switchTo(id);
    setCloseOpen(true);
  };

  const closeOthers = (keepId) => {
    let kept = 0;
    tabs.forEach((t) => { if (t.id === keepId) return; if (infoOf(t).dirty) { kept += 1; return; } closePublication(t.id); });
    if (kept) toast.info(`${kept} publication${kept === 1 ? '' : 's'} with unsaved changes ${kept === 1 ? 'was' : 'were'} kept open`);
  };
  const closeSaved = () => {
    const clean = tabs.filter((t) => !infoOf(t).dirty && !infoOf(t).pristine);
    if (!clean.length) { toast.info('Every open publication has unsaved changes'); return; }
    clean.forEach((t) => closePublication(t.id));
  };

  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const to = tabs.findIndex((t) => t.id === targetId);
    rawDispatch({ type: 'MOVE_DOC_TAB', payload: { id: dragId, to } });
    setDragId(null);
  };

  // Display order: grouped by folder (first-appearance order of the groups, the user's order inside each group).
  const entries = tabs.map((t) => { const info = infoOf(t); return { t, info, key: info.folder?.id || NO_FOLDER }; });
  let ordered = entries;
  if (grouped) {
    const groups = new Map();
    entries.forEach((en) => { if (!groups.has(en.key)) groups.set(en.key, []); groups.get(en.key).push(en); });
    ordered = [...groups.values()].flat();
  }
  const showDividers = grouped && new Set(entries.map((en) => en.key)).size > 1; // a single group needs no labels
  const groupColor = (en) => (en.info.folder ? folderColorCss(en.info.folder.color) : 'hsl(var(--muted-foreground))');
  const renderDivider = (en) => (
    <div key={`group-${en.key}`} role="presentation" className={`self-center shrink-0 inline-flex items-center gap-1 ${inBackstage ? 'pl-2 pr-1' : 'pl-1.5 pr-0.5'} max-w-[140px]`} title={en.info.folder ? `Folder: ${en.info.folder.path}` : 'Not in a folder'} data-testid={inBackstage ? `backstage-document-tab-group-${en.key}` : `document-tab-group-${en.key}`}>
      <span className="h-4 w-[3px] rounded-full shrink-0" style={{ background: groupColor(en) }} aria-hidden="true" />
      <span className="text-[10px] font-semibold uppercase tracking-wide truncate" style={{ color: groupColor(en) }}>{en.info.folder ? en.info.folder.name : 'No folder'}</span>
    </div>
  );

  return (
    <div className={`${inBackstage ? 'wp-doctabs-backstage rounded-md border border-border h-10 flex items-center gap-1 px-2 mb-5 max-w-5xl' : 'wp-doctabs h-8 flex items-end gap-0.5 px-1.5'} overflow-x-auto overflow-y-hidden wp-ribbon-scroll [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`} role="tablist" aria-label="Open publications" data-testid={inBackstage ? 'document-tabs-backstage' : 'document-tabs'} data-count={tabs.length}>
      {inBackstage && <span className="text-[11px] font-medium text-muted-foreground shrink-0 pr-1">Open publications</span>}
      {ordered.map((en, i) => {
        const { t, info } = en;
        const isActive = t.id === active;
        const divider = showDividers && (i === 0 || ordered[i - 1].key !== en.key) ? renderDivider(en) : null;
        return (
          <React.Fragment key={t.id}>
          {divider}
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <div
                role="tab"
                tabIndex={0}
                aria-selected={isActive}
                draggable
                onDragStart={() => setDragId(t.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(t.id)}
                onDragEnd={() => setDragId(null)}
                onClick={() => switchTo(t.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); switchTo(t.id); } if (e.key === 'Delete') requestClose(t.id); }}
                onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); requestClose(t.id); } }}
                title={`${info.name}${info.dirty ? ' — unsaved changes' : info.saved ? ' — saved to the cloud' : ' — not saved yet'}${info.folder ? ` · in ${info.folder.path}` : ''}`}
                className={`group relative h-7 min-w-[120px] max-w-[220px] shrink-0 flex items-center gap-1.5 pl-2.5 pr-1 text-[12px] cursor-pointer select-none transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${inBackstage ? `rounded-md ${isActive ? 'bg-primary/10 text-foreground border border-primary/40 font-medium' : 'text-muted-foreground border border-transparent hover:bg-muted hover:text-foreground'}` : `rounded-t-md ${isActive ? 'bg-[hsl(var(--wp-ribbon-2))] text-foreground border border-b-0 border-[hsl(var(--wp-ribbon-border))] -mb-px font-medium' : 'text-muted-foreground hover:bg-[hsl(var(--wp-command-hover))] hover:text-foreground'}`} ${dragId === t.id ? 'opacity-50' : ''}`}
                data-testid={inBackstage ? `backstage-document-tab-${t.id}` : `document-tab-${t.id}`}
                data-active={isActive ? '1' : undefined}
                data-dirty={info.dirty ? '1' : undefined}
                data-folder={info.folder ? info.folder.id : undefined}
                style={info.folder ? { boxShadow: `inset 0 2px 0 0 ${folderColorCss(info.folder.color)}` } : undefined}
              >
                {info.saved ? <Cloud className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" /> : <FileText className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden="true" />}
                {info.folder && <span className="h-2 w-2 rounded-full shrink-0" style={{ background: folderColorCss(info.folder.color) }} aria-label={`In folder ${info.folder.path}`} title={`In ${info.folder.path}`} data-testid={inBackstage ? `backstage-document-tab-folder-${t.id}` : `document-tab-folder-${t.id}`} />}
                <span className="truncate flex-1" data-testid={inBackstage ? `backstage-document-tab-name-${t.id}` : `document-tab-name-${t.id}`}>{info.name}</span>
                {info.dirty && <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" aria-label="Unsaved changes" data-testid={inBackstage ? `backstage-document-tab-dirty-${t.id}` : `document-tab-dirty-${t.id}`} />}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); requestClose(t.id); }}
                  className={`h-5 w-5 shrink-0 rounded-sm inline-flex items-center justify-center hover:bg-destructive/15 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${isActive ? '' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'}`}
                  aria-label={`Close ${info.name}`}
                  title="Close publication"
                  data-testid={inBackstage ? `backstage-document-tab-close-${t.id}` : `document-tab-close-${t.id}`}
                ><X className="h-3 w-3" /></button>
              </div>
            </ContextMenuTrigger>
            <ContextMenuContent className="w-56 z-[95]" data-testid={inBackstage ? `backstage-document-tab-menu-${t.id}` : `document-tab-menu-${t.id}`}>
              <ContextMenuItem onClick={() => requestClose(t.id)} className="text-[13px]" data-testid="document-tab-menu-close">Close</ContextMenuItem>
              <ContextMenuItem onClick={() => closeOthers(t.id)} disabled={tabs.length < 2} className="text-[13px]" data-testid="document-tab-menu-close-others">Close Others</ContextMenuItem>
              <ContextMenuItem onClick={closeSaved} className="text-[13px]" data-testid="document-tab-menu-close-saved">Close Saved Publications</ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuItem onClick={() => { switchTo(t.id); rawDispatch({ type: 'SET_FILE_OPEN', payload: { open: true, section: 'save' } }); }} className="text-[13px]" data-testid="document-tab-menu-save">Save…</ContextMenuItem>
              <ContextMenuItem onClick={() => rawDispatch({ type: 'SET_FILE_OPEN', payload: { open: true, section: 'new' } })} className="text-[13px]" data-testid="document-tab-menu-new">New Publication…</ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuCheckboxItem checked={grouped} onCheckedChange={toggleGrouped} className="text-[13px]" data-testid="document-tab-menu-group">Group tabs by folder</ContextMenuCheckboxItem>
            </ContextMenuContent>
          </ContextMenu>
          </React.Fragment>
        );
      })}
      <button
        type="button"
        onClick={() => rawDispatch({ type: 'SET_FILE_OPEN', payload: { open: true, section: 'new' } })}
        className="h-7 w-7 shrink-0 self-center rounded-md inline-flex items-center justify-center text-muted-foreground hover:bg-[hsl(var(--wp-command-hover))] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        title="New publication (opens in a new tab)"
        aria-label="New publication"
        data-testid={inBackstage ? 'backstage-document-tab-new' : 'document-tab-new'}
      ><Plus className="h-4 w-4" /></button>
      {!inBackstage && <span className="ml-auto self-center pr-2 text-[11px] text-muted-foreground whitespace-nowrap hidden md:inline" data-testid="document-tabs-hint">{tabs.length} open{showDividers ? ' · grouped by folder' : ''} · publications stay open until you close their tab</span>}
      <ClosePublicationDialog open={closeOpen} onOpenChange={setCloseOpen} onClosed={() => toast.success('Publication closed')} />
    </div>
  );
};
